import axios, { type AxiosInstance } from "axios";

import { isAccountInactive } from "@/lib/authFailure";
import { logoutUser } from "@/lib/authHelpers";
import { getAccessToken } from "@/lib/session";

/**
 * Authenticated axios instance — the single HTTP client for API calls
 * (PLX §6). Base URL + Bearer behaviour preserved from the previous
 * `dev` data layer (VITE_API_BASE_URL, token from the client session).
 *
 * `withCredentials: true` is REQUIRED: the PayGate auth flow keeps the
 * "password OK" state in an HTTP session (JSESSIONID). The cookie the
 * server sets on POST /api/auth/login must be sent back on
 * /api/auth/otp/send and /api/auth/otp/validate — otherwise the server
 * replies "Please login with username and password first to send OTP".
 * Every auth step goes through this single instance, so the cookie is
 * carried across all three. (Cross-origin cookies also require the
 * backend to send Access-Control-Allow-Credentials: true and a specific
 * Access-Control-Allow-Origin — not `*`.)
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data && !(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

/** Pages that are *about* signing in — a 401 there is the answer, not an eviction. */
const SESSION_EXEMPT_PATHS = ["/login", "/login-with-token", "/verify-otp", "/unauthorized"];

// Parallel queries all fail together when a session dies; only the first one
// gets to navigate.
let endingSession = false;

/**
 * A 401 means this session is over — either the token is gone/expired, or the
 * account behind it was switched off while the tab was open.
 *
 * Both cases sign out through `logoutUser`, which blocklists the token
 * server-side first. Simply navigating away would leave a credential that
 * still works for 24h in the hands of anything holding a copy — the exact
 * window the deactivation was meant to close.
 */
async function endSession(response: unknown): Promise<void> {
  if (typeof window === "undefined" || endingSession) return;
  const path = window.location.pathname;
  if (SESSION_EXEMPT_PATHS.some((exempt) => path.startsWith(exempt))) return;

  endingSession = true;
  const reason = isAccountInactive(response) ? "inactive" : "session";
  await logoutUser(undefined, `/unauthorized?reason=${reason}`);
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      void endSession(error.response);
    }
    return Promise.reject(error);
  }
);
